import React from 'react';
import AppModel from '../DataModel/AppModel';
import ServerAPI from '../ServerAPI';
import Promise from 'bluebird';
import User from '../DataModel/User';
import {transaction} from 'mobx';

// Creating the login module from Auth0, which will be called to delegate
// loggin the user in.
var lock = null

function logout(history) {
  // Request to clear the permissions for the logged in token:
  ServerAPI.clearPermissionCache()
    .then(function () {
      console.log("Server has been requested to clear cached permissions.");
      return null;
    })
    .catch(function (err) {
      console.log("Request for clearing cached permission threw error :", err);
    });
  localStorage.removeItem('userProfile');
  localStorage.removeItem('userToken');
  ServerAPI.setToken(null);
  AppModel.resetAllData();
  if(history) { history.push('/loggedOut'); }
}

// When client is logged in, we fetch the set of roles and permissions
// of the current user as they may influence the UI presentation:
// This returns a promise of having loaded the required profile data
function loadPermissionsAndRoles() {
  // TODO : We could bundle these three calls into a single call and
  // only do one round-trip.
  var promises = [];
  AppModel.fetchingUserProfile = true;
  promises.push(
    ServerAPI.getUserPermissions(AppModel.id_customer, AppModel.id_user)
      .then(function (permlist) {        
        AppModel.permissions = permlist;
      })
  );
  promises.push(
    ServerAPI.getUserRoles(AppModel.id_customer, AppModel.id_user)
      .then(function (rolelist) {
        AppModel.roles = rolelist;
      })
  );
  promises.push(
    ServerAPI.getUserInfo(AppModel.id_customer, AppModel.id_user)
      .then(function (info) {
        // TODO: It seems that the User objects use id_home_location
        // but the data base / server returns a home_location property
        console.log("ServerAPI.getUserInfo returned", info);
        info.id_home_location = info.home_location;
        transaction(() => {
          AppModel.userInfo = new User();
          AppModel.userInfo.copyFieldsFrom(info);
          AppModel.time_zone = info.timezone;
          AppModel.locale = info.locale_code;
        })
      })
  );
  return Promise.all(promises)
    .finally(function () {
      AppModel.fetchingUserProfile = false;
    });
}

// Returns a promise of having tried to get a logged in token from
// local storage.
// DataModel.loggedIn will be set to true/false depending on the outcome
function loadIdToken() {
  var idToken = localStorage.getItem('userToken');

  // If we have a token that is not expired :
  if (idToken && !ServerAPI.checkTokenExpired(idToken)) {

    ServerAPI.setToken(idToken);
    var profile = ServerAPI.getUserProfileFromToken(idToken);
    // If we don't get a profile something is wrong. Give up:

    if (profile == null) {
      ServerAPI.setToken(null);
      AppModel.loggedIn = false;
      // Nothing to wait for, we failed at getting token from storage:
      console.log("Null profile in token.");
      return Promise.resolve();
    }
    AppModel.login_email = profile.email;
    AppModel.login_email_verified = profile.email_verified;
    // If this is an impersonation login:
    AppModel.impersonator = profile.impersonator || null;

    if (profile.id_customer == null) {
      AppModel.missingUserProfile = true;
      AppModel.loggedIn = true;
      // Nothing to wait for:
      return Promise.resolve();
    }
    else {
      AppModel.id_user = profile.id_user;
      AppModel.id_customer = profile.id_customer;
      // We wait to mark user as logged in until we have loaded the
      // user profile and role+permission data
      return loadPermissionsAndRoles()
        .then(() => {
          if (!AppModel.permissions || AppModel.permissions.length == 0) {
            console.log("Users have no permissions, thus no login allowed");
          }
          else {
            AppModel.loggedIn = true;
          }
          return Promise.resolve();
        });
    }
  }
  else {
    ServerAPI.setToken(null);
    AppModel.loggedIn = false;
    // Nothing to wait for, we failed at getting token from storage:
    return Promise.resolve();
  }
}

function tokenExpiredHandler() {
  AppModel.loggedIn = false;
  localStorage.removeItem('userProfile');
  localStorage.removeItem('userToken');
  ServerAPI.setToken(null);
  AppModel.resetAllData();
}

// This may require showing the loging-popup box or not, depending on cookie
// state.
function login(history) {
  return new Promise(function (resolve, reject) {
    // First we try to login using stored token :
    resolve(loadIdToken().then(() => {
      // Successfully got a token from storage and loaded profile:
      if (!AppModel.loggedIn) {
        // Redirect to base url to avoid having to add all urls as allowed urls in Auth0 settings
        if (window.location.pathname.indexOf('/rui') === 0) {
          if (window.location.pathname.length > 5) {
            history.push('/');
            window.location.reload();
          }
        } else if (window.location.pathname.length > 1) {
          history.push('/');
          window.location.reload();
        }

        setLockIfNotSet();
        return lock.show();
      }
    }));
  })

}

// Forward request to server API to trigger server-side to resend a
// account-email verification flow:
function resendVerificationEmail() {
  return ServerAPI.resendVerificationEmailForUser(AppModel.login_email);
}

// If we have a valid login token for another user than the current one,
// we can switch to that user without re-loading client :
function switchUser(token, history) {
  // First we wipe all loaded data:
  AppModel.resetAllData();
  // Remove the previous user
  localStorage.removeItem('userProfile');
  localStorage.removeItem('userToken');
  // Store the new token :
  localStorage.setItem('userToken', token);
  // And schedule a re-initialization based on the stored token:
  setTimeout(function () {
    loadIdToken()
      .then(function () {
        history.push("/home");
        return null;
      });
  }, 1);
}

function setLockIfNotSet() {
  if (lock === null) {
    lock = new Auth0Lock(
      AppModel.config.AUTH0_CLIENT_ID,
      AppModel.config.AUTH0_DOMAIN,
      {
        closable: false,
        rememberLastLogin: true,
        theme: {
          logo: ( process.env.NODE_ENV == 'test' ?
            'https://deconx-test.herokuapp.com/DeconX-logo-badge.png' :
            'https://data-dx.deconx.com/DeconX-logo-badge.png' ),
        },
        auth: {
          sso: true,
          redirect: false,
          params: {
            scope: 'openid profile email app_metadata'
          }
        },
        languageDictionary: {
          title: "Log in"
        },
        configurationBaseUrl: 'https://cdn.auth0.com'
      }
    );

    lock.on('authorization_error', function(error) {
      console.log(error);
    });
    lock.on('unrecoverable_error', function(error) {
      console.log(error);
    });    

    lock.on('authenticated', function (authResult) {
      console.log(authResult);
      if (!authResult || !authResult.idToken || !authResult.idTokenPayload) {
        appState.loggedIn = false;
        throw new Error();
      }

      localStorage.setItem('userProfile', JSON.stringify(profile));
      localStorage.setItem('userToken', authResult.idToken);
      ServerAPI.setToken(authResult.idToken);
      var profile = ServerAPI.getUserProfileFromToken(authResult.idToken);

      // If we get a null profile, give up.
      if (profile == null) {
        console.error('Authenticated', 'Got a null profile.');
        ServerAPI.setToken(null);
        AppModel.loggedIn = false;
        throw new Error();
      }
      AppModel.login_email = profile.email;
      AppModel.login_email_verified = profile.email_verified;
      if (profile.id_customer == null) {
        AppModel.missingUserProfile = true;
        AppModel.loggedIn = true;
      } else {
        AppModel.id_user = profile.id_user;
        AppModel.id_customer = profile.id_customer;
        // We wait to mark user as logged in until we have loaded the
        // user profile and role+permission data
        loadPermissionsAndRoles().then(() => {
          AppModel.loggedIn = true;
          // Setting loggedIn to true, triggers loading the datasets that have
          // been declared required for the routes.  This happens in sync with
          // setting loggedIn = true, which causes loading promises to be
          // created, which again makes bluebird believe that we forgot to
          // return them from this method.  So to avoid warning output about
          // run-away promises without returns, we need to add this null return:
        });
      }

      lock.hide();
    });
  }
}

ServerAPI.setExpiredCallback(tokenExpiredHandler);

export {lock, logout, login, switchUser, resendVerificationEmail};



/** WEBPACK FOOTER **
 ** ./src/utils/LoginHelper.js
 **/